import { nanoid } from "nanoid";
import { fabric } from "fabric";
import { Layer, LayerType } from "@xmiles/holopix-types";
import { CANVAS_OBJECT_CONTROL_CONFIG, CANVAS_OBJECT_FIELDS, isPlaceholderObject } from "../../helper/canvasConfig";
import { ensureFabricVideoRegistered } from "./video";
import { ensureFabricModel3dRegistered } from "./model3d";

export const isImageOrVideoType = (type: unknown): type is "image" | "video" | "model3d" => {
  return type === "image" || type === "video" || type === "model3d";
};

/**
 * 图片/视频/3D 模型对象的基础类型
 * 包含 type 和 src 属性（src 可选，因为部分降级对象可能暂无 src）
 */
export type ImageOrVideoObject = { type: "image" | "video" | "model3d"; src?: string; [key: string]: any };

export const isImageOrVideoObject = (obj: unknown): obj is ImageOrVideoObject => {
  return isImageOrVideoType((obj as any)?.type);
};

// 图层类型计数器
const layerCounters: Record<string, number> = {};

/**
 * 生成名称
 */
export const generateLayerName = (layerName: string) => {
  if (!layerCounters[layerName]) {
    layerCounters[layerName] = 0;
  }
  layerCounters[layerName]++;
  return `${layerName}_${layerCounters[layerName]}`;
};

/**
 * 生成CID
 */
export const generateLayerCid = () => {
  return `layer_${nanoid()}`;
};

/**
 * 动态扫描画布，获取编组的下一个可用序号
 * 扫描画布所有 group 对象，匹配 /^new_group_(\d+)$/ 格式的 name
 * 返回最大序号 + 1，若画布为空或无匹配项则返回 1
 * @param canvas Fabric.js 画布实例
 * @returns 下一个可用序号（从 1 开始）
 */
export const getNextGroupIndex = (canvas: fabric.Canvas): number => {
  if (!canvas) return 1;

  const regex = /^new_group_(\d+)$/;
  let maxN = 0;

  // 递归扫描函数，遍历对象（包括 Group 嵌套子对象）
  const scanObjects = (objects: fabric.Object[]) => {
    for (const obj of objects) {
      const name = (obj as any).name;
      if (typeof name === 'string') {
        const match = name.match(regex);
        if (match) {
          const n = parseInt(match[1], 10);
          if (n > maxN) maxN = n;
        }
      }
      // 递归扫描 Group 子对象
      if (obj.type === 'group' && (obj as fabric.Group).getObjects) {
        scanObjects((obj as fabric.Group).getObjects());
      }
    }
  };

  scanObjects(canvas.getObjects());
  return maxN + 1;
};

/**
 * 动态扫描画布，获取指定前缀的下一个可用序号
 * 扫描画布所有对象（递归 Group 子对象），匹配 /^前缀\s*(\d+)$/ 格式的 name
 * 支持带空格格式（如 "PSD 1"）和不带空格格式（如 "PSD1"）
 * 返回最大序号 + 1，若画布为空或无匹配项则返回 1
 * @param canvas Fabric.js 画布实例
 * @param prefix 名称前缀，如 "图片" 或 "PSD"
 * @returns 下一个可用序号（从 1 开始）
 */
export const getNextImageIndex = (canvas: fabric.Canvas, prefix: string): number => {
  if (!canvas) return 1;

  // 支持带空格和不带空格两种格式，便于向后兼容
  const regex = new RegExp(`^${prefix}\\s*(\\d+)$`);
  let maxN = 0;

  // 递归扫描函数，遍历对象（包括 Group 嵌套子对象）
  const scanObjects = (objects: fabric.Object[]) => {
    for (const obj of objects) {
      const name = (obj as any).name;
      if (typeof name === 'string') {
        const match = name.match(regex);
        if (match) {
          const n = parseInt(match[1], 10);
          if (n > maxN) maxN = n;
        }
      }
      // 递归扫描 Group 子对象
      if (obj.type === 'group' && (obj as fabric.Group).getObjects) {
        scanObjects((obj as fabric.Group).getObjects());
      }
    }
  };

  scanObjects(canvas.getObjects());
  return maxN + 1;
};

/**
 * 生成图层结构
 * @param layerType 图层类型
 * @param layerData 图层自定义数据（会覆盖默认值）
 * @param options 可选配置项
 * @param options.name 显式指定图层名称，传入时跳过 generateLayerName 调用；不传时保持原有行为
 */
export const generateLayer = (
  layerType: LayerType,
  layerData?: Partial<Layer>,
  options?: { name?: string }
): Layer => {
  const layerName = options?.name || generateLayerName('new_layer');
  const layerCid = generateLayerCid();

  const defaultLayer: Layer = {
    id: layerCid,
    name: layerName,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    visible: true,
    lock: false,
    type: layerType,
    src: '',
  };

  return {
    ...defaultLayer,
    ...layerData
  };
};

/**
 * 从序列化数据重建 Fabric 对象
 * 统一处理对象重建逻辑，确保自定义属性正确复制
 * @param objectData 对象序列化数据（来自 toObject）
 * @param options 重建选项
 * @returns 重建后的 Fabric 对象
 */
export const rebuildObjectFromData = (
  objectData: any,
  options: {
    newId?: string;
    newName?: string;
    positionOffset?: { x: number; y: number };
    imageElement?: HTMLImageElement | null;
  } = {}
): fabric.Object | null => {
  try {
    // 解构并移除需要重新生成的字段
    const { id, name, left, top, ...otherData } = objectData;

    // 计算新位置
    const newLeft = (left || 0) + (options.positionOffset?.x || 0);
    const newTop = (top || 0) + (options.positionOffset?.y || 0);

    let cloned: fabric.Object | null = null;

    // 根据类型创建对象
    if (isImageOrVideoType(otherData.type) && options.imageElement) {
      // 使用提供的图片元素创建 Image 对象
      if (otherData.type === "video") {
        ensureFabricVideoRegistered();
        const VideoKlass = ((fabric as any).Video || fabric.Image) as any;
        cloned = new VideoKlass(options.imageElement, {
          ...otherData,
          type: "video",
          left: newLeft,
          top: newTop
        });
        (cloned as any).type = "video";
      } else if (otherData.type === "model3d") {
        ensureFabricModel3dRegistered();
        const Model3dKlass = ((fabric as any).Model3d || fabric.Image) as any;
        cloned = new Model3dKlass(options.imageElement, {
          ...otherData,
          type: "model3d",
          left: newLeft,
          top: newTop
        });
        (cloned as any).type = "model3d";
      } else {
        cloned = new fabric.Image(options.imageElement, {
          ...otherData,
          left: newLeft,
          top: newTop
        });
      }
    } else if (otherData.type === "group") {
      // Group 类型需要特殊处理 - 递归重建子对象
      const childrenData = otherData.objects || [];
      const clonedChildren = childrenData
        .map((childData: any) => rebuildObjectFromData(childData))
        .filter((c: fabric.Object | null): c is fabric.Object => c !== null);

      // 移除 objects 属性，因为已经手动重建了子对象
      const { objects, ...groupDataWithoutObjects } = otherData;

      cloned = new fabric.Group(clonedChildren, {
        ...groupDataWithoutObjects,
        left: newLeft,
        top: newTop
      });
    } else {
      // 其他基础形状
      const className = otherData.type;
      if (!className) return null;

      if (className === "video") {
        ensureFabricVideoRegistered();
      }

      const capitalizedName = className.charAt(0).toUpperCase() + className.slice(1);
      // @ts-ignore
      const FabricClass = fabric[capitalizedName];
      if (FabricClass) {
        cloned = new FabricClass({
          ...otherData,
          left: newLeft,
          top: newTop
        });
      }
    }

    if (!cloned) return null;

    // 检测是否为占位符
    const isPlaceholder = otherData.isPlaceholder;

    // 根据对象类型选择控制配置
    const controlConfig = isPlaceholder
      ? CANVAS_OBJECT_CONTROL_CONFIG.placeholder
      : CANVAS_OBJECT_CONTROL_CONFIG.normal;

    // 设置基础属性和控制配置
    cloned.set({
      id: options.newId,
      name: options.newName,
      ...controlConfig
    });

    return cloned;
  } catch (err) {
    console.warn("重建对象失败:", objectData.type, err);
    return null;
  }
};

/**
 * 异步从序列化数据重建 Fabric 对象
 * 适用于粘贴等需要加载图片资源的场景
 * @param objectData 对象序列化数据
 * @param options 重建选项
 * @returns Promise<重建后的 Fabric 对象>
 */
export const rebuildObjectFromDataAsync = (
  objectData: any,
  options: {
    newId?: string;
    newName?: string;
    positionOffset?: { x: number; y: number };
  } = {}
): Promise<fabric.Object | null> => {
  return new Promise((resolve) => {
    try {
      // 1. 计算新位置
      const newLeft = (objectData.left || 0) + (options.positionOffset?.x || 0);
      const newTop = (objectData.top || 0) + (options.positionOffset?.y || 0);

      // 2. 使用 enlivenObjects 恢复对象 (支持图片异步加载)
      // 注意：enlivenObjects 会自动处理 Group 及其子对象
      fabric.util.enlivenObjects(
        [objectData],
        (enlivenedObjects: fabric.Object[]) => {
          if (!enlivenedObjects || enlivenedObjects.length === 0) {
            resolve(null);
            return;
          }

          const cloned = enlivenedObjects[0];

          // 3. enlivenObjects 可能丢失非 fabric 内置的自定义属性，需显式回写
          for (const key of CANVAS_OBJECT_FIELDS) {
            if (key === "id" || key === "name") continue;
            if (objectData[key] !== undefined && (cloned as any)[key] === undefined) {
              (cloned as any)[key] = objectData[key];
            }
          }

          // 4. 应用新属性
          const isPlaceholder = objectData.isPlaceholder;

          const controlConfig = isPlaceholder
            ? CANVAS_OBJECT_CONTROL_CONFIG.placeholder
            : CANVAS_OBJECT_CONTROL_CONFIG.normal;

          cloned.set({
            id: options.newId,
            name: options.newName,
            left: newLeft,
            top: newTop,
            ...controlConfig
          });
          
          cloned.setCoords();

          resolve(cloned);
        },
        "fabric"
      );
    } catch (err) {
      console.warn("异步重建对象失败:", objectData.type, err);
      resolve(null);
    }
  });
};

export const countCanvasImageObjects = (objects: any[] | null | undefined): number => {
  if (!Array.isArray(objects)) return 0;
  let total = 0;
  for (const obj of objects) {
    if (!obj) continue;
    if (obj.id === "workspace") continue;
    if ((obj as any).temporaryLayerInfo) continue;
    if (obj.visible === false) continue;
    if (isPlaceholderObject(obj)) continue;

    if (isImageOrVideoObject(obj)) {
      total += 1;
      continue;
    }

    if (obj.type === "group" && Array.isArray((obj as any).objects)) {
      total += countCanvasImageObjects((obj as any).objects);
    }
  }
  return total;
};
